/**
 * @file Renders the step that verifies the mobile code and enables two factor.
 */
import React, { useState } from "react";
import { Typography, Button, Space } from "antd";

import { W1 } from "@components/Constants";
import { Value } from "@components/Text";
import { useAppContext } from "@components/AppContext";
import type { AppContext, Operation } from "@components/AppContext/types";
import VerificationCodeInput from "./VerificationCodeInput";

const LABEL_SUBMIT = "Verify mobile number";
const LABEL_RESEND = "Resend verification code";
const LABEL_ERROR = "Invalid or expired verification code";
const LABEL_DESCRIPTION_LINE_1 = "We sent a verification code to your mobile number";
const LABEL_DESCRIPTION_LINE_2 = "Kindly check your incoming messages and enter it below.";

type Props = {
  mobileNumber: string;
  onSuccess: () => Promise<void> | void;
  enableOperation: Operation;
  verificationRequestId: string;
  onResendVerificationCode: () => void;
}

type RequestError = {
  code?: string;
}

/**
 * Renders the verification code input and the actions that enable two
 * factor authentication or resend the code.
 */
const ActivateVerificationRequest = ({
  mobileNumber,
  onSuccess,
  enableOperation,
  verificationRequestId,
  onResendVerificationCode,
}: Props) => {
  const [ isLoading, setIsLoading ] = useState(false);
  const [ verificationCode, setVerificationCode ] = useState("");

  const { request, showErrorMessage } = useAppContext() as AppContext;

  /**
   * Submits the verification code and reports an invalid or expired code.
   */
  const activateVerificationRequest = async () => {
    const parameters = {
      verificationCode,
      verificationRequestId,
    };

    try {
      await request(enableOperation, parameters, [ "UnprocessibleConditionError" ]);
      await onSuccess();

    } catch (error) {
      const requestError = error as RequestError;
      const isExpected = requestError.code === "UnprocessibleConditionError";

      if (!isExpected) {
        console.error(error);
        return;
      }

      showErrorMessage(LABEL_ERROR);
    }
  };

  /**
   * Activates the verification request when the code has a valid length.
   */
  const onClick = async () => {
    if (!isVerificationCodeValid) {
      return;
    }

    setIsLoading(true);
    await activateVerificationRequest();
    setIsLoading(false);
  };

  /**
   * Stores the verification code as the person types it.
   */
  const onChange = (value: string) => {
    setVerificationCode(value);
  };

  const isVerificationCodeValid = verificationCode.length === 6;

  return (
    <>
      <Typography.Paragraph>
        <Typography.Text>
          {LABEL_DESCRIPTION_LINE_1}{" "}
          <Typography.Text strong><Value value={mobileNumber} /></Typography.Text>.{" "}
          {LABEL_DESCRIPTION_LINE_2}
        </Typography.Text>
      </Typography.Paragraph>

      <center>
        <Space direction="vertical" size={W1}>
          <VerificationCodeInput onChange={onChange} />

          <Button
            size="large"
            type="primary"
            loading={isLoading}
            onClick={onClick}
            disabled={!isVerificationCodeValid}
          >
            {LABEL_SUBMIT}
          </Button>

          <Button
            type="link"
            onClick={onResendVerificationCode}
          >
            {LABEL_RESEND}
          </Button>
        </Space>
      </center>
    </>
  );
};

export default ActivateVerificationRequest;
